import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { User } from '../../../../../services';
import { FileAcceptType, useTags } from '../../../../hooks';
import { GalleryItem } from '../../../../models';
import { getCssVar } from '../../../../utils';
import { AuthorInfo } from '../../../AuthorInfo';
import { FilePreview, FileUpload } from '../../../File';
import { IconLabel } from '../../../IconLabel';
import { TextAreaField } from '../../../TextAreaField';
import { UserMentionPreview } from '../../../UserMentionPreview';
import { CreateCardSettingsTabType, LinkPostQuoteFields } from '../../CreateCardModal';

import classNames from 'classnames';
import classes from './LinkPostQuote.module.scss';

interface LinkPostQuoteProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  url: string;
  quoteData: LinkPostQuoteFields;
  setQuote: (quote: LinkPostQuoteFields) => void;
  setSettingsOpen: (isOpen: boolean) => void;
  canOpenSettings: boolean;
  setActiveLinkPostSettingsTabIndex: (index: number) => void;
  setLinkPostQuoteGalleryItems: (galleryItems: GalleryItem[]) => void;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
}

export const LinkPostQuote: FunctionComponent<LinkPostQuoteProps> = memo(
  ({
    loading,
    setLoading,
    url,
    quoteData,
    setQuote,
    setSettingsOpen,
    canOpenSettings,
    setActiveLinkPostSettingsTabIndex,
    setLinkPostQuoteGalleryItems,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
  }) => {
    const { t } = useTranslation();

    const { hasContentTags } = useTags();

    const headlineRef = useRef<HTMLTextAreaElement>(null);

    const {
      headline,
      quote,
      quotePerson,
      quotePersonHandle,
      quotePersonImage,
      quoteSource,
      gallery: linkPostQuoteGalleryItems,
    } = quoteData;

    const hasGallery = useMemo(
      () => Boolean(linkPostQuoteGalleryItems.length),
      [linkPostQuoteGalleryItems.length]
    );

    const hasTagsShortcut = canOpenSettings && hasContentTags;

    useEffect(() => {
      if (headlineRef.current) {
        headlineRef.current.setSelectionRange(
          headlineRef.current.value.length,
          headlineRef.current.value.length
        );
        headlineRef.current.focus();
      }
    }, []);

    const fieldChangeHandler = useCallback(
      (name: keyof LinkPostQuoteFields, value: string) => {
        setQuote({ ...quoteData, [name]: value });
      },
      [quoteData, setQuote]
    );

    const headerSubTitle = useMemo(
      () => `@${quotePersonHandle} via ${quoteSource.toLowerCase()}`,
      [quotePersonHandle, quoteSource]
    );

    const tagsClickHandler = useCallback(() => {
      setActiveLinkPostSettingsTabIndex(CreateCardSettingsTabType.TAGS);
      setSettingsOpen(true);
    }, [setActiveLinkPostSettingsTabIndex, setSettingsOpen]);

    const content = useMemo(() => {
      return (
        <>
          <div className={classes['link-post-quote__url']}>
            <span className={classes['link-post-quote__url-label']}>{t('common.link')}:</span>
            <a
              href={url}
              target={'_blank'}
              rel={'noreferrer'}
              className={classes['link-post-quote__url-value']}
            >
              {url}
            </a>
          </div>

          <TextAreaField
            ref={headlineRef}
            inputClassName={classes['link-post-quote__headline']}
            value={headline}
            placeholder={t('addContent.link-post-quote-headline')}
            maxLength={360}
            onChange={({ target }) => fieldChangeHandler('headline', target.value)}
          />

          <AuthorInfo
            avatarUrl={quotePersonImage?.url}
            title={quotePerson}
            subTitle={headerSubTitle}
            className={classes['link-post-quote__author-info']}
          />

          <UserMentionPreview
            mentionedUsers={mentionedUsers}
            {...(postInStoryAllowMentions && { onClick: () => setUserMentionModalOpen(true) })}
          />

          <FileUpload
            galleryItems={linkPostQuoteGalleryItems}
            setGalleryItems={setLinkPostQuoteGalleryItems}
            loading={loading}
            setLoading={setLoading}
            fileAcceptTypes={[FileAcceptType.IMAGE]}
            uploadLimit={20}
            multiUpload
          />

          <FilePreview
            galleryItems={linkPostQuoteGalleryItems}
            setGalleryItems={setLinkPostQuoteGalleryItems}
            hasSettings
            hideCaption
          />

          <TextAreaField
            inputClassName={classes['link-post-quote__content']}
            value={quote}
            placeholder={`${t('addContent.link-post-quote-content')} ${!hasGallery ? '*' : ''}`}
            maxLength={2000}
            onChange={({ target }) => fieldChangeHandler('quote', target.value)}
          />
        </>
      );
    }, [
      fieldChangeHandler,
      hasGallery,
      headerSubTitle,
      headline,
      linkPostQuoteGalleryItems,
      loading,
      mentionedUsers,
      postInStoryAllowMentions,
      quote,
      quotePerson,
      quotePersonImage?.url,
      setLinkPostQuoteGalleryItems,
      setLoading,
      setUserMentionModalOpen,
      t,
      url,
    ]);

    return (
      <div
        className={classNames(classes['link-post-quote'], {
          [classes['link-post-quote--mobile']]: isMobileOnly,
        })}
      >
        <div
          className={classNames(classes['link-post-quote__scrollable'], {
            [classes['link-post-quote__scrollable--mobile']]: isMobileOnly,
            [classes['link-post-quote__scrollable--has-tags']]: hasTagsShortcut,
          })}
        >
          {content}
        </div>

        <div className={classes['link-post-quote__pane']}>
          <div className={classes['link-post-quote__pane-fields']}>
            {postInStoryAllowMentions && (
              <IconLabel
                iconId={'mention'}
                iconSize={20}
                label={t('addContent.mention')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={() => setUserMentionModalOpen(true)}
              />
            )}
            {hasTagsShortcut && (
              <IconLabel
                iconId={'tags'}
                iconSize={20}
                label={t('addContent.tags')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={tagsClickHandler}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
